import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import { baseUrl } from 'utils/axios'
import { formatParams, handleErrors } from 'utils/helpers'
import {
  IScheduleAnalyticsParams,
  IScheduleByDepartmentSingleResponse,
  IScheduleByRoleSingleResponse,
  IScheduleDailyCoverageSingleResponse,
  IScheduleSummaryResponse,
} from 'types/api/hrAnalyticsApi.interface'
import { IHrAnalyticsState } from './hrAnalytics.types'

const initialState: IHrAnalyticsState = {
  isLoading: false,
  scheduleSummary: null,
  scheduleByDepartment: [],
  scheduleByRole: [],
  scheduleDailyCoverage: [],
}

export const getScheduleSummary = createAsyncThunk(
  'hrAnalytics/getScheduleSummary',
  async (params: IScheduleAnalyticsParams, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IScheduleSummaryResponse> = await baseUrl.get(
        'hr/analytics/schedule/summary',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getScheduleByDepartment = createAsyncThunk(
  'hrAnalytics/getScheduleByDepartment',
  async (params: IScheduleAnalyticsParams, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IScheduleByDepartmentSingleResponse[]> = await baseUrl.get(
        'hr/analytics/schedule/by-department',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getScheduleByRole = createAsyncThunk(
  'hrAnalytics/getScheduleByRole',
  async (params: IScheduleAnalyticsParams, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IScheduleByRoleSingleResponse[]> = await baseUrl.get(
        'hr/analytics/schedule/by-role',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getScheduleDailyCoverage = createAsyncThunk(
  'hrAnalytics/getScheduleDailyCoverage',
  async (params: IScheduleAnalyticsParams, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IScheduleDailyCoverageSingleResponse[]> = await baseUrl.get(
        'hr/analytics/schedule/daily-coverage',
        { params },
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const hrAnalyticsSlice = createSlice({
  name: 'hrAnalytics',
  initialState,
  reducers: {
    clearHrAnalytics: () => initialState,
  },
  extraReducers: {
    [getScheduleSummary.pending.type]: (state) => {
      state.isLoading = true
    },
    [getScheduleSummary.fulfilled.type]: (
      state,
      { payload }: { payload: IScheduleSummaryResponse },
    ) => {
      state.isLoading = false
      state.scheduleSummary = payload
    },
    [getScheduleSummary.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getScheduleByDepartment.pending.type]: (state) => {
      state.isLoading = true
    },
    [getScheduleByDepartment.fulfilled.type]: (
      state,
      { payload }: { payload: IScheduleByDepartmentSingleResponse[] },
    ) => {
      state.isLoading = false
      state.scheduleByDepartment = payload
    },
    [getScheduleByDepartment.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getScheduleByRole.pending.type]: (state) => {
      state.isLoading = true
    },
    [getScheduleByRole.fulfilled.type]: (
      state,
      { payload }: { payload: IScheduleByRoleSingleResponse[] },
    ) => {
      state.isLoading = false
      state.scheduleByRole = payload
    },
    [getScheduleByRole.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getScheduleDailyCoverage.pending.type]: (state) => {
      state.isLoading = true
    },
    [getScheduleDailyCoverage.fulfilled.type]: (
      state,
      { payload }: { payload: IScheduleDailyCoverageSingleResponse[] },
    ) => {
      state.isLoading = false
      state.scheduleDailyCoverage = payload
    },
    [getScheduleDailyCoverage.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearHrAnalytics } = hrAnalyticsSlice.actions
export default hrAnalyticsSlice.reducer
